import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { ASSETS } from "../../data/landing";
import { useLanguage } from "../../i18n/LanguageContext";
import StoreButtons from "./StoreButtons";

const KEY_NEVER = "hob_popup_never";
const KEY_LAST_CLOSED = "hob_popup_last_closed";
const FIRST_DELAY = 15000; // 15s after first visit
const REPEAT_DELAY = 180000; // every 3 minutes after being closed

export const DownloadPopup = () => {
  const { t } = useLanguage();
  const p = t.popup;
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const schedule = (delay) => {
    if (localStorage.getItem(KEY_NEVER)) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), Math.max(delay, 0));
  };

  useEffect(() => {
    if (localStorage.getItem(KEY_NEVER)) return;
    const lastClosed = localStorage.getItem(KEY_LAST_CLOSED);
    if (!lastClosed) {
      schedule(FIRST_DELAY);
    } else {
      const elapsed = Date.now() - Number(lastClosed);
      schedule(REPEAT_DELAY - elapsed);
    }
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(KEY_LAST_CLOSED, String(Date.now()));
    schedule(REPEAT_DELAY);
  };

  const handleDownload = () => {
    localStorage.setItem(KEY_NEVER, "1");
    clearTimeout(timerRef.current);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="download-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#2A1112]/50 backdrop-blur-sm"
            onClick={handleClose}
            data-testid="download-popup-overlay"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={p.heading}
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-3xl overflow-hidden rounded-[2rem] bg-[#FCFAF7] shadow-[0_60px_120px_-30px_rgba(123,17,19,0.5)]"
          >
            <button
              onClick={handleClose}
              data-testid="download-popup-close"
              aria-label="Close"
              className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[#7B1113] shadow-sm backdrop-blur transition-colors duration-300 hover:bg-white"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Left: screenshots */}
              <div className="relative hidden overflow-hidden bg-[#7B1113] md:block">
                <div className="pointer-events-none absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#F5A623]/30 blur-3xl" />
                <div className="relative flex h-full items-center justify-center p-8">
                  <div className="relative">
                    <img
                      src={ASSETS.screenAstro}
                      alt="Hari Om Bol astrologers"
                      loading="lazy"
                      className="w-40 -rotate-6 rounded-2xl border-4 border-[#2A1112] shadow-2xl"
                    />
                    <img
                      src={ASSETS.screenSaathi}
                      alt="Hari Om Bol Saathi"
                      loading="lazy"
                      className="absolute -right-16 top-8 w-40 rotate-6 rounded-2xl border-4 border-[#2A1112] shadow-2xl"
                    />
                  </div>
                </div>
              </div>

              {/* Right: content */}
              <div className="flex flex-col justify-center p-8 md:p-10">
                <img
                  src={ASSETS.logo}
                  alt={t.brand}
                  loading="lazy"
                  className="mb-5 h-12 w-12 rounded-full object-cover shadow-[0_6px_16px_-6px_rgba(123,17,19,0.4)]"
                />
                <h3
                  className="text-2xl font-semibold leading-tight tracking-tight text-[#2A1112] md:text-[1.75rem]"
                  data-testid="download-popup-heading"
                >
                  {p.heading}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#5E4646]">
                  {p.text}
                </p>

                <div className="mt-7">
                  <StoreButtons testidPrefix="popup" onDownload={handleDownload} />
                </div>

                <button
                  onClick={handleClose}
                  data-testid="download-popup-maybe-later"
                  className="mt-5 self-start text-sm font-medium text-[#5E4646] underline-offset-4 transition-colors duration-300 hover:text-[#7B1113] hover:underline"
                >
                  {p.maybeLater}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DownloadPopup;
